import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, FileDown, Landmark, RefreshCw } from 'lucide-react';
import api from 'services/api';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (value) => {
  if (!value || value === '-') return '-';
  const brMatch = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (brMatch) return `${brMatch[1]}/${brMatch[2]}/${brMatch[3]}`;
  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
  if (!value || value === '-') return '-';
  const trimmed = String(value).trim();
  const brMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (brMatch) {
    const [, d, m, y, hh = '00', mm = '00'] = brMatch;
    return `${d}/${m}/${y} ${hh}:${mm}`;
  }
  return formatDate(trimmed);
};

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseBrDateToISO = (value) => {
  const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return null;
  const [, d, m, y] = match;
  return `${y}-${m}-${d}`;
};

const normalizarDatasetN8n = (dataset) => {
  if (!dataset) return [];
  if (!Array.isArray(dataset)) return [dataset];
  return dataset.flatMap((item) => (Array.isArray(item) ? item : [item]));
};

const extrairContasAPagar = (value, depth = 0) => {
  if (!value || depth > 3) return [];

  if (Array.isArray(value)) {
    return value.flatMap((v) => extrairContasAPagar(v, depth + 1));
  }

  if (typeof value === 'object') {
    const lista = value['contas a pagar'];
    if (Array.isArray(lista)) {
      const looksLikeConta = lista.some(
        (item) =>
          item &&
          typeof item === 'object' &&
          (Object.prototype.hasOwnProperty.call(item, 'id') ||
            Object.prototype.hasOwnProperty.call(item, 'codigo') ||
            Object.prototype.hasOwnProperty.call(item, 'descricao'))
      );

      if (looksLikeConta) return lista;
      return lista.flatMap((v) => extrairContasAPagar(v, depth + 1));
    }
  }

  return [];
};

const normalizarRespostaN8n = (payload) => {
  if (Array.isArray(payload)) return payload[0] ?? null;
  return payload ?? null;
};

const ContasPagasDia = ({ onExportPdf, exportingPdf = false, canExportPdf = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [summary, setSummary] = useState({
    total_entradas: 0,
    total_saidas: 0,
    saldo_total: 0,
    total_registros: 0,
    previsao_amanha_total: 0,
    previsao_amanha_registros: 0,
    previsao_armazenada_disponivel: false,
    previsao_armazenada_valor: 0,
    previsao_armazenada_data: null,
  });

  const carregarDados = async () => {
    setLoading(true);
    setError(null);

    try {
      const hojeISO = toISODate(new Date());
      const amanha = new Date();
      amanha.setDate(amanha.getDate() + 1);
      const amanhaISO = toISODate(amanha);

      const [pagasResponse, contasResponse, previsaoResponse, previsaoArmazenadaResponse] = await Promise.all([
        api.post(
          '/n8n/administracao/a_pagar',
          {
            action: 'pagas',
            data_inicio: hojeISO,
            data_fim: hojeISO,
          },
          { timeout: 130000 }
        ),
        api.post('/n8n/adm/list_contas', {}),
        api.post(
          '/n8n/administracao/a_pagar',
          {
            action: 'em_aberto',
            data_inicio: amanhaISO,
            data_fim: amanhaISO,
          },
          { timeout: 130000 }
        ),
        api.post('/n8n/administracao/relatorios/contas-pagas-dia/previsao', {
          data: hojeISO,
        }).catch(() => null),
      ]);

      const pagasPayload = normalizarRespostaN8n(pagasResponse?.data);
      if (!pagasPayload || pagasPayload?.success === false) {
        setRows([]);
        setContasBancarias([]);
        setSummary({
          total_entradas: 0,
          total_saidas: 0,
          saldo_total: 0,
          total_registros: 0,
          previsao_amanha_total: 0,
          previsao_amanha_registros: 0,
          previsao_armazenada_disponivel: false,
          previsao_armazenada_valor: 0,
          previsao_armazenada_data: null,
        });
        setError(pagasPayload?.message || 'Erro ao carregar contas pagas');
        return;
      }

      const contasBrutas = contasResponse?.data || [];
      let contasLista = [];
      if (Array.isArray(contasBrutas)) {
        contasBrutas.forEach((item) => {
          if (item?.dados && Array.isArray(item.dados)) {
            contasLista = contasLista.concat(item.dados);
          } else if (Array.isArray(item)) {
            contasLista = contasLista.concat(item);
          }
        });
      } else if (contasBrutas?.data && Array.isArray(contasBrutas.data)) {
        contasLista = contasBrutas.data;
      }
      setContasBancarias(contasLista);

      const contaById = new Map(
        contasLista.map((c) => [String(c?.id ?? c?.ID ?? c?.id_conta ?? ''), c?.nome || c?.conta || c?.descricao || null])
      );

      const dadosBrutos = normalizarDatasetN8n(pagasPayload?.data || []);
      const contasPagas = extrairContasAPagar(dadosBrutos);

      const mapa = {};
      contasLista.forEach((contaBanco) => {
        const idConta = Number(contaBanco?.id ?? contaBanco?.ID ?? contaBanco?.id_conta ?? 0);
        if (!idConta) return;

        mapa[idConta] = {
          ...contaBanco,
          conta_nome: contaBanco?.nome || contaBanco?.conta || contaBanco?.descricao || contaById.get(String(idConta)) || 'Conta não informada',
          contas_pagas: [],
          total_valor: 0,
          total_contas: 0,
          ultima_data_pagamento: null,
        };
      });

      contasPagas.forEach((contaPaga) => {
        const idConta = Number(contaPaga?.id_contas ?? contaPaga?.id_conta ?? 0);
        const valor = Number(contaPaga?.valor || contaPaga?.valor_pago || contaPaga?.valor_total_pago || 0);
        if (!idConta) return;

        if (!mapa[idConta]) {
          mapa[idConta] = {
            id: idConta,
            conta_nome: contaPaga?.conta || contaPaga?.nome_conta || contaPaga?.conta_bancaria || `Conta ${idConta}`,
            contas_pagas: [],
            total_valor: 0,
            total_contas: 0,
            ultima_data_pagamento: null,
          };
        }

        mapa[idConta].contas_pagas.push(contaPaga);
        mapa[idConta].total_valor += valor;
        mapa[idConta].total_contas += 1;

        const dataPagamento = contaPaga?.data_pagamento || null;
        if (dataPagamento) {
          if (!mapa[idConta].ultima_data_pagamento) {
            mapa[idConta].ultima_data_pagamento = dataPagamento;
          } else {
            const atual = new Date(String(mapa[idConta].ultima_data_pagamento).split(' ')[0].replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
            const proxima = new Date(String(dataPagamento).split(' ')[0].replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
            if (!Number.isNaN(proxima.getTime()) && (Number.isNaN(atual.getTime()) || proxima > atual)) {
              mapa[idConta].ultima_data_pagamento = dataPagamento;
            }
          }
        }
      });

      const linhas = Object.values(mapa)
        .filter((contaBanco) => contaBanco.contas_pagas.length > 0)
        .sort((a, b) => b.total_valor - a.total_valor)
        .map((contaBanco) => ({
          conta: contaBanco.conta_nome,
          total_contas: contaBanco.total_contas,
          total_valor: contaBanco.total_valor,
          ultima_data_pagamento: contaBanco.ultima_data_pagamento || '-',
          contas_pagas: [...contaBanco.contas_pagas]
            .map((item) => ({
              fornecedor: item?.fornecedor || 'Fornecedor não informado',
              valor: Number(item?.valor || item?.valor_pago || item?.valor_total_pago || 0),
              data_pagamento: item?.data_pagamento || '-',
              documento: item?.documento || '-',
              tipo_pagamento: item?.tipo_pagamento || '-',
              observacao: item?.observacao || item?.obs || '-',
            }))
            .sort((a, b) => b.valor - a.valor),
        }));

      const dadosPrevisao = previsaoResponse?.data?.data || [];
      let contasPrevisao = [];
      dadosPrevisao.forEach((item) => {
        if (item['contas a pagar'] && Array.isArray(item['contas a pagar'])) {
          contasPrevisao = contasPrevisao.concat(item['contas a pagar']);
        }
      });
      const contasAmanha = contasPrevisao.filter((conta) => {
        const dataVenc = parseBrDateToISO(conta?.data_vencimento || conta?.vencimento || '');
        if (!dataVenc) return true;
        return dataVenc === amanhaISO;
      });
      const previsaoAmanhaTotal = contasAmanha.reduce(
        (acc, conta) => acc + Number(conta?.valor || conta?.valor_aberto || 0),
        0
      );

      const previsaoArmazenada = previsaoArmazenadaResponse?.data?.data?.previsao_armazenada || {};

      setRows(linhas);
      setSummary({
        total_entradas: linhas.reduce((acc, item) => acc + Number(item.total_valor || 0), 0),
        total_saidas: linhas.reduce((acc, item) => acc + Number(item.saida || 0), 0),
        saldo_total: linhas.reduce((acc, item) => acc + Number(item.total_valor || 0), 0),
        total_registros: linhas.reduce((acc, item) => acc + Number(item.total_contas || 0), 0),
        previsao_amanha_total: previsaoAmanhaTotal,
        previsao_amanha_registros: contasAmanha.length,
        previsao_armazenada_disponivel: Boolean(previsaoArmazenada?.disponivel),
        previsao_armazenada_valor: Number(previsaoArmazenada?.previsao_amanha || 0),
        previsao_armazenada_data: previsaoArmazenada?.forecast_date || null,
      });
    } catch (err) {
      setRows([]);
      setContasBancarias([]);
      setSummary({
        total_entradas: 0,
        total_saidas: 0,
        saldo_total: 0,
        total_registros: 0,
        previsao_amanha_total: 0,
        previsao_amanha_registros: 0,
        previsao_armazenada_disponivel: false,
        previsao_armazenada_valor: 0,
        previsao_armazenada_data: null,
      });
      setError(err?.response?.data?.message || 'Erro ao carregar contas pagas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    setExpandedRows({});
  }, []);

  useEffect(() => {
    const onCompanyChange = () => carregarDados();
    window.addEventListener('company-context-changed', onCompanyChange);
    window.addEventListener('company-changed', onCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', onCompanyChange);
      window.removeEventListener('company-changed', onCompanyChange);
    };
  }, []);

  const dataReferencia = useMemo(() => formatDate(toISODate(new Date())), []);

  const cards = useMemo(() => ([
    {
      id: 'pagas',
      label: 'Total Pago',
      value: formatCurrency(summary.total_entradas),
      valueClass: 'text-[color:var(--status-good)]',
    },
    {
      id: 'registros',
      label: 'Qtde. de Registros',
      value: Number(summary.total_registros || 0).toLocaleString('pt-BR'),
      valueClass: 'text-[color:var(--text-primary)]',
    },
    {
      id: 'periodo',
      label: 'Data de Referência',
      value: dataReferencia,
      valueClass: 'text-[color:var(--text-primary)]',
    },
    {
      id: 'reserva',
      label: 'Contas Bancárias',
      value: Number(contasBancarias.length || 0).toLocaleString('pt-BR'),
      valueClass: 'text-[color:var(--text-primary)]',
    },
    {
      id: 'previsao-amanha',
      label: 'Previsão Amanhã',
      value: formatCurrency(summary.previsao_amanha_total),
      valueClass: 'text-[color:var(--status-warn)]',
      subtitle: `${Number(summary.previsao_amanha_registros || 0).toLocaleString('pt-BR')} conta(s)`,
    },
    {
      id: 'previsao-armazenada',
      label: 'Prev. Armazenada',
      value: summary.previsao_armazenada_disponivel
        ? formatCurrency(summary.previsao_armazenada_valor)
        : '-',
      valueClass: 'text-[color:var(--accent)]',
      subtitle: summary.previsao_armazenada_disponivel && summary.previsao_armazenada_data
        ? formatDate(summary.previsao_armazenada_data)
        : 'Sem registro anterior',
    },
  ]), [summary, contasBancarias.length, dataReferencia]);

  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  return (
    <div className="overflow-hidden rounded-lg bg-transparent border-0 sm:bg-[color:var(--surface)] sm:border sm:border-[color:var(--border)]">
      <div className="px-1 py-1.5 sm:border-b sm:border-[color:var(--border)] sm:bg-[color:var(--surface-muted)] sm:px-4 sm:py-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="text-sm font-medium text-[color:var(--text-primary)]">
            Relatório de contas pagas no dia
          </div>
          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <button
              type="button"
              onClick={onExportPdf}
              className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-[color:var(--accent)] px-3 py-2 text-sm text-[color:var(--avatar-text)] transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40 sm:h-auto sm:flex-none sm:justify-start"
              title={canExportPdf ? 'Exportar PDF' : 'Sem permissão para exportar'}
              disabled={loading || exportingPdf || !canExportPdf}
            >
              <FileDown className="h-4 w-4" />
              {exportingPdf ? 'Gerando...' : 'Exportar PDF'}
            </button>
            <button
              type="button"
              onClick={carregarDados}
              className="rounded-md p-2 text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface)] hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
              title="Atualizar"
              aria-label="Atualizar relatório de contas pagas"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="pt-2 space-y-4 sm:p-4 md:p-5">
        {error && (
          <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm text-[color:var(--status-bad)]">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 2xl:grid-cols-6">
          {cards.map((card) => (
            <div
              key={card.id}
              className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-2.5 min-h-[82px] sm:px-3 sm:py-3 sm:min-h-[92px]"
            >
              <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">{card.label}</div>
              <div className={`mt-3 text-sm font-semibold leading-tight sm:mt-6 sm:text-lg ${card.valueClass}`}>{card.value}</div>
              <div className="mt-1 text-[11px] text-[color:var(--text-muted)] min-h-[16px] sm:min-h-[18px]">
                {card.subtitle || ' '}
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-lg md:border md:border-[color:var(--border)]">
          <div className="flex items-center gap-2 px-1 py-1.5 text-sm font-medium text-[color:var(--text-primary)] md:border-b md:border-[color:var(--border)] md:bg-[color:var(--surface-muted)] md:px-3 md:py-2">
            <Landmark className="h-4 w-4" />
            Contas pagas no período
          </div>

          <div className="mt-1 divide-y divide-[color:var(--border)]/55 rounded-lg border border-[color:var(--border)]/70 bg-[color:var(--surface)] md:hidden">
            {rows.length === 0 && !loading ? (
              <div className="px-3 py-6 text-center text-sm text-[color:var(--text-muted)]">
                Nenhuma correlação encontrada entre contas pagas e contas bancárias
              </div>
            ) : (
              rows.map((row, idx) => {
                const rowKey = `${row.conta}-${idx}`;
                const isExpanded = !!expandedRows[rowKey];

                return (
                  <div key={rowKey}>
                    <button
                      type="button"
                      className="w-full px-3 py-3 text-left transition-colors hover:bg-[color:var(--surface-muted)]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                      onClick={() => toggleRow(rowKey)}
                      aria-expanded={isExpanded}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-[color:var(--text-primary)]">{row.conta || '-'}</div>
                          <div className="mt-1 text-xs text-[color:var(--text-muted)]">
                            Último pagamento: {formatDate(String(row.ultima_data_pagamento || '').split(' ')[0])}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)]" />
                        ) : (
                          <ChevronRight className="h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)]" />
                        )}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Contas pagas</div>
                          <div className="mt-0.5 text-sm font-semibold text-[color:var(--text-primary)]">
                            {Number(row.total_contas || 0).toLocaleString('pt-BR')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-muted)]">Total pago</div>
                          <div className="mt-0.5 text-sm font-semibold text-[color:var(--status-good)]">
                            {formatCurrency(row.total_valor)}
                          </div>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[color:var(--border)] bg-[color:var(--surface-muted)]/15 px-3 py-2.5">
                        <div className="mb-2 text-xs font-semibold text-[color:var(--text-muted)]">
                          Pagamentos ({row.contas_pagas.length} registro{row.contas_pagas.length !== 1 ? 's' : ''})
                        </div>
                        <div className="space-y-1.5">
                          {row.contas_pagas.map((item, itemIdx) => (
                            <div
                              key={`${rowKey}-item-${itemIdx}`}
                              className="rounded-md bg-[color:var(--surface)]/85 px-2.5 py-2"
                            >
                              <div className="text-sm font-semibold text-[color:var(--text-primary)]">{item.fornecedor}</div>
                              {item.observacao && item.observacao !== '-' ? (
                                <div className="mt-0.5 text-xs text-[color:var(--text-muted)]">{item.observacao}</div>
                              ) : null}
                              <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                                <div className="text-[color:var(--text-muted)]">Documento</div>
                                <div className="truncate text-right text-[color:var(--text-primary)]">{item.documento}</div>
                                <div className="text-[color:var(--text-muted)]">Tipo</div>
                                <div className="truncate text-right text-[color:var(--text-primary)]">{item.tipo_pagamento}</div>
                              </div>
                              <div className="mt-1.5 flex items-center justify-between border-t border-[color:var(--border)]/70 pt-1.5">
                                <div className="text-xs text-[color:var(--text-muted)]">{formatDateTime(item.data_pagamento)}</div>
                                <div className="text-sm font-semibold text-[color:var(--status-good)]">{formatCurrency(item.valor)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center justify-between border-t border-[color:var(--border)] pt-2">
                          <span className="text-xs font-semibold text-[color:var(--text-muted)]">Total da conta</span>
                          <span className="text-sm font-bold text-[color:var(--status-good)]">{formatCurrency(row.total_valor)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-[color:var(--border)] text-sm">
              <thead className="bg-[color:var(--surface-muted)]">
                <tr className="text-left text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">
                  <th className="px-3 py-2 font-semibold">Conta Bancária</th>
                  <th className="px-3 py-2 text-right font-semibold">Contas Pagas</th>
                  <th className="px-3 py-2 text-right font-semibold">Total Pago</th>
                  <th className="px-3 py-2 font-semibold">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Último Pagamento
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border)]">
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-[color:var(--text-muted)]" colSpan={4}>
                      Nenhuma correlação encontrada entre contas pagas e contas bancárias
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const rowKey = `${row.conta}-${idx}`;
                    const isExpanded = !!expandedRows[rowKey];

                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="cursor-pointer text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                          onClick={() => toggleRow(rowKey)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleRow(rowKey);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                        >
                          <td className="px-3 py-2">
                            <div className="inline-flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-[color:var(--text-muted)]" />
                              )}
                              <span className="font-medium">{row.conta || '-'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right text-[color:var(--text-primary)]">{Number(row.total_contas || 0).toLocaleString('pt-BR')}</td>
                          <td className="px-3 py-2 text-right font-semibold text-[color:var(--status-good)]">{formatCurrency(row.total_valor)}</td>
                          <td className="px-3 py-2">{formatDate(String(row.ultima_data_pagamento || '').split(' ')[0])}</td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-[color:var(--surface-muted)]/25">
                            <td className="px-3 py-3" colSpan={4}>
                              <div className="text-xs font-semibold text-[color:var(--text-muted)] mb-2">
                                Pagamentos ({row.contas_pagas.length} registro{row.contas_pagas.length !== 1 ? 's' : ''})
                              </div>
                              <div className="overflow-x-auto rounded-md border border-[color:var(--border)]">
                                <table className="min-w-full divide-y divide-[color:var(--border)] text-sm">
                                  <thead className="bg-[color:var(--surface-muted)]">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">
                                      <th className="px-3 py-2 font-semibold">Fornecedor</th>
                                      <th className="px-3 py-2 font-semibold">Documento</th>
                                      <th className="px-3 py-2 font-semibold">Tipo</th>
                                      <th className="px-3 py-2 font-semibold">Data Pagamento</th>
                                      <th className="px-3 py-2 text-right font-semibold">Valor</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[color:var(--border)]">
                                    {row.contas_pagas.map((item, itemIdx) => (
                                      <tr key={`${rowKey}-item-${itemIdx}`} className="text-[color:var(--text-primary)]">
                                        <td className="px-3 py-2">
                                          <div className="font-medium">{item.fornecedor}</div>
                                          {item.observacao && item.observacao !== '-' ? (
                                            <div className="text-xs text-[color:var(--text-muted)]">{item.observacao}</div>
                                          ) : null}
                                        </td>
                                        <td className="px-3 py-2">{item.documento}</td>
                                        <td className="px-3 py-2">{item.tipo_pagamento}</td>
                                        <td className="px-3 py-2">{formatDateTime(item.data_pagamento)}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-[color:var(--status-good)]">{formatCurrency(item.valor)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="bg-[color:var(--surface-muted)]/70 text-[color:var(--text-primary)]">
                                      <td className="px-3 py-2 font-semibold" colSpan={4}>Total da conta</td>
                                      <td className="px-3 py-2 text-right font-bold text-[color:var(--status-good)]">{formatCurrency(row.total_valor)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContasPagasDia;
